import type { Author } from '../../../mocks/authors';

interface AuthorHeroProps {
  author: Author;
  articleCount: number;
}

export default function AuthorHero({ author, articleCount }: AuthorHeroProps) {
  return (
    <section className="pt-28 pb-0 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-zinc-600 mb-10">
          <a href="/blog" className="hover:text-zinc-400 transition-colors cursor-pointer">Blog</a>
          <i className="ri-arrow-right-s-line" />
          <span className="text-zinc-500">Profil autora</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 pb-16 border-b border-zinc-800/60">
          {/* Photo */}
          <div className="flex-shrink-0 flex justify-center lg:block">
            <div className="relative">
              <div className="w-44 h-44 lg:w-52 lg:h-52 rounded-2xl overflow-hidden border-2 border-orange-500/25">
                <img
                  src={author.image}
                  alt={author.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Badge */}
              <div className="absolute -bottom-3 -right-3 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                {author.badge}
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-orange-400 text-xs uppercase tracking-widest font-semibold mb-3">Autor článkov</p>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-2 leading-tight">{author.name}</h1>
            <p className="text-zinc-400 text-base mb-5">{author.role}</p>

            <p className="text-zinc-400 text-sm leading-relaxed mb-7 max-w-2xl">{author.longBio}</p>

            {/* Skills */}
            <div className="flex flex-wrap gap-2 mb-7">
              {author.skills.map((skill) => (
                <span
                  key={skill}
                  className="text-zinc-400 text-xs bg-zinc-800/70 border border-zinc-700/50 px-3 py-1.5 rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>

            {/* Social + website */}
            <div className="flex flex-wrap items-center gap-3">
              {author.social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={s.label}
                  className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700/60 text-zinc-300 hover:text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${s.icon} text-orange-400 text-sm`} />
                  {s.label}
                </a>
              ))}
              {author.websiteHref && (
                <a
                  href={author.websiteHref}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-300 text-sm transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-external-link-line" />
                  {author.websiteLabel}
                </a>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="lg:w-56 flex-shrink-0">
            <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-6 grid grid-cols-2 lg:grid-cols-1 gap-5">
              {[
                ...author.stats.slice(0, 3),
                { value: String(articleCount), label: 'Článkov na blogu' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-2xl font-black text-orange-400 leading-none mb-1">{s.value}</div>
                  <div className="text-zinc-500 text-xs leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
